import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { SSO_URL } from "../../Constants/Constants";
import AxiosInstance from "../../services/AxiosInstance";
import { fetchData } from "../../services/UtilService";
import { useAlert } from "./../alerts/AlertContext";

function SSO() {
  const showAlert = useAlert();

  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    registrationId: "",
    identityProvider: "",
    acs: "",
    idpEntity: "",
    ssoUrl: "",
    cert: "",
    active: "true",
  });

  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({
    registrationId: false,
    identityProvider: false,
    acs: false,
    idpEntity: false,
    ssoUrl: false,
    cert: false,
    active: false,
  });

  useEffect(() => {
    loadSSO();
  }, []);

  const loadSSO = async () => {
    setLoading(true);
    try {
      const data = await fetchData(SSO_URL);
      if (data.length > 0) {
        const ssoData = data[0];
        setFormData({
          id: ssoData.id,
          registrationId: ssoData.registrationId || "",
          identityProvider: ssoData.identityProvider || "",
          acs: ssoData.acs || "",
          idpEntity: ssoData.idpEntity || "",
          ssoUrl: ssoData.ssoUrl || "",
          cert: ssoData.cert || "",
          active: ssoData.active ? "true" : "false",
        });

        setTouched({
          registrationId: false,
          identityProvider: false,
          acs: false,
          idpEntity: false,
          ssoUrl: false,
          cert: false,
          active: false,
        });

        setFormErrors({});
      }
    } catch (error) {
      showAlert("Failed to load SSO configuration", "error");
      console.error("Failed to load SSO data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    const error = validateField(field, value);
    setFormErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateField = (field, value) => {
    switch (field) {
      case "registrationId":
        return !value.trim() ? "Registration ID is required" : "";
      case "identityProvider":
        return !value.trim() ? "Identity Provider is required" : "";
      case "acs":
        return !value.trim() ? "ACS is required" : "";
      case "idpEntity":
        return !value.trim() ? "IDP Entity is required" : "";
      case "ssoUrl":
        return !value.trim() ? "SSO URL is required" : "";
      case "cert":
        return !value.trim() ? "Certificate is required" : "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((field) => {
      if (field !== "id" && field !== "active") {
        const error = validateField(field, formData[field]);
        if (error) {
          errors[field] = error;
          setTouched((prev) => ({
            ...prev,
            [field]: true,
          }));
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showAlert("Please fix the errors before saving", "warning");
      return;
    }

    setSaving(true);
    try {
      const ssoData = {
        ...formData,
        active: formData.active === "true",
      };

      if (formData.id) {
        await AxiosInstance.put(`${SSO_URL}${formData.id}`, ssoData);
        showAlert("SSO configuration updated successfully!", "success");
      } else {
        await AxiosInstance.post(SSO_URL, ssoData);
        showAlert("SSO configuration created successfully!", "success");
      }

      await loadSSO();
    } catch (error) {
      showAlert("Failed to save SSO configuration", "error");
      console.error("Error saving SSO:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadSSO();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 0, maxWidth: 1200, mx: "auto", borderRadius: 0 }}>
      {/* Identity Provider Configuration */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 0 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Registration ID"
              value={formData.registrationId}
              onChange={(e) =>
                handleInputChange("registrationId", e.target.value)
              }
              error={!!formErrors.registrationId && touched.registrationId}
              helperText={
                touched.registrationId ? formErrors.registrationId : ""
              }
              placeholder="Enter registration ID"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Identity Provider"
              value={formData.identityProvider}
              onChange={(e) =>
                handleInputChange("identityProvider", e.target.value)
              }
              error={!!formErrors.identityProvider && touched.identityProvider}
              helperText={
                touched.identityProvider ? formErrors.identityProvider : ""
              }
              placeholder="Enter identity provider name"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ACS (Assertion Consumer Service)"
              value={formData.acs}
              onChange={(e) => handleInputChange("acs", e.target.value)}
              error={!!formErrors.acs && touched.acs}
              helperText={touched.acs ? formErrors.acs : ""}
              placeholder="Enter ACS URL"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="IDP Entity"
              value={formData.idpEntity}
              onChange={(e) => handleInputChange("idpEntity", e.target.value)}
              error={!!formErrors.idpEntity && touched.idpEntity}
              helperText={touched.idpEntity ? formErrors.idpEntity : ""}
              placeholder="Enter IDP entity ID"
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="SSO URL"
              value={formData.ssoUrl}
              onChange={(e) => handleInputChange("ssoUrl", e.target.value)}
              error={!!formErrors.ssoUrl && touched.ssoUrl}
              helperText={touched.ssoUrl ? formErrors.ssoUrl : ""}
              placeholder="https://your-idp.com/sso"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset" fullWidth>
              <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                {" "}
                Status
              </Typography>
              <RadioGroup
                row
                name="active"
                value={formData.active}
                onChange={(e) => handleInputChange("active", e.target.value)}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Enabled"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Disabled"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={3} sx={{ mt: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Certificate"
              value={formData.cert}
              onChange={(e) => handleInputChange("cert", e.target.value)}
              error={!!formErrors.cert && touched.cert}
              helperText={
                touched.cert
                  ? formErrors.cert
                  : "Enter the X.509 certificate for SAML authentication"
              }
              variant="standard"
              placeholder="-----BEGIN CERTIFICATE-----..."
              multiline
              rows={4}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 4,
          p: 3,
        }}
      >
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </Box>
    </Paper>
  );
}

export default SSO;
